import { definedMapWithDefault } from "collection-utils";
import { asNumber, asString } from "common/components/component-helpers";
import type { Value } from "common/components/types";
import { ActionSource, LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useForceValueProducers } from "hooks/use-value-producers";
import * as React from "react";
import RatingComponent from "../ui/smart/rating/rating";

export const RatingMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[valuePV, onChangePV, captionPV, maxRatingPV, ratingUnitPV], [valueProducer]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );
    const { onClick: onChangeCB } = useOnClickFromAction(onChangePV, ActionSource.DateTimeField, ctx, env);

    const convertOut = React.useCallback((output: number): Value => {
        return output ?? output;
    }, []);

    const onChange = React.useCallback(
        async (newValue: number) => {
            if (valueProducer === undefined) return;

            valueProducer.set(convertOut(newValue));
            if (onChangeCB !== undefined) {
                void onChangeCB(undefined);
            }
        },
        [convertOut, valueProducer, onChangeCB]
    );

    if (captionPV === LOADING || valuePV === LOADING || onChangePV === LOADING) {
        return env.renderProgress();
    }

    const value = definedMapWithDefault(valuePV, 0, asNumber);
    const caption = definedMapWithDefault(captionPV, "", asString);
    const maxRating = definedMapWithDefault(maxRatingPV, 3, asNumber);
    const ratingUnit = definedMapWithDefault(ratingUnitPV, "stars", asString);

    return (
        <RatingComponent
            ratingValue={value}
            caption={caption}
            onChange={onChange}
            maxRating={maxRating}
            ratingUnit={ratingUnit}
        />
    );
};
